// The Accounts page (/app/accounts): a summary strip, then every account. Prop accounts with
// rules get a wide "rule-guarded" card with a live meter per rule; personal and stock accounts
// get a P&L card. Each shows how its trades get in: synced from TopstepX (topstepx.js), with
// its status and a Sync button, or imported from files.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { api } from './api';
import { accountStatus } from './balance';
import { AdjustBalanceDialog, canAdjustBalance } from './CashFlows';
import { ArchiveAccountDialog, asksHowItEnded } from './AccountEnded';
import { maskNumber } from './Accounts';
import { formatMoney, netPnl, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { Icon } from './Icons';
import { LogoBadge, accountLogo } from './Logos';
import { detectPlan, rulesFrom } from './AddAccountWizard';

// A small badge for each platform's initials, shown when there's no logo (Logos.js).
const PLATFORM_BADGE = {
  TopstepX: { text: 'TX', className: 'pf-topstepx' },
  Tradovate: { text: 'TV', className: 'pf-tradovate' },
  NinjaTrader: { text: 'NT', className: 'pf-ninjatrader' },
  Rithmic: { text: 'R', className: 'pf-rithmic' },
  Tradesea: { text: 'TS', className: 'pf-tradesea' },
  Other: { text: '•', className: 'pf-other' },
};

const VIEW_KEY = 'mm-accounts-view';
const SORT_KEY = 'mm-accounts-sort';
const read = (key, fallback) => { try { return localStorage.getItem(key) || fallback; } catch { return fallback; } };
const write = (key, value) => { try { localStorage.setItem(key, value); } catch { /* private window */ } };

const k = (n) => (n >= 1000 ? `${Math.round(n / 1000)}K` : String(n));
const pct = (x) => `${Math.round(x * 100)}%`;
const clamp = (x) => Math.max(0, Math.min(1, x));

function when(date) {
  if (!date) return '—';
  return new Date(date).toLocaleString(currentLocale(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
const clock = (date) => (date ? new Date(date).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' }) : '—');

// How an account's trades get in, and how that's going.
function syncInfo(account, sync, trades, ib) {
  // Interactive Brokers (ibkr.js): the same shape, with the IBKR account number as the name.
  const ibLink = ib && ib.connected ? (ib.links || []).find((l) => l.accountId === account._id) : null;
  if (ibLink) {
    const last = ibLink.lastSyncAt || ib.lastSyncAt;
    return {
      kind: 'auto', provider: 'ibkr', state: ib.lastError ? 'error' : 'synced', last, link: { tsxName: `IBKR ${ibLink.ibAccount}` },
      next: last && !ib.lastError ? new Date(new Date(last).getTime() + (ib.syncMinutes || 60) * 60000) : null,
      message: ib.lastError || null,
      open: ibLink.open || 0,
    };
  }
  const link = sync && sync.connected ? (sync.links || []).find((l) => l.accountId === account._id) : null;
  if (link) {
    const last = link.lastSyncAt || sync.lastSyncAt;
    const state = sync.lastError ? 'error' : link.alreadyIn ? 'attention' : 'synced';
    return {
      kind: 'auto', provider: 'topstepx', state, last, link,
      next: last && !sync.lastError ? new Date(new Date(last).getTime() + (sync.syncMinutes || 15) * 60000) : null,
      message: sync.lastError || null,
    };
  }
  const lastImport = trades.reduce((m, t) => (t.createdAt && t.createdAt > m ? t.createdAt : m), '');
  return { kind: 'file', state: trades.length ? 'imported' : 'empty', last: lastImport || null, next: null };
}

const SYNC_LABEL = { synced: 'Live sync · Synced', error: 'Sync error', attention: 'Needs attention', imported: 'File import · Up to date', empty: 'No trades yet' };

const isFuturesSymbol = (s) => /[FGHJKMNQUVXZ]\d{1,2}$/.test(String(s || '').toUpperCase());
const hasRules = (a) => a.type !== 'Personal' && Boolean(a.maxLossLimit || a.profitTarget || a.dailyLossLimit || a.consistencyLimit);

// Which group an account belongs to: prop (rule-guarded), personal or stocks.
function groupOf(account, trades) {
  if (account.type === 'Evaluation' || account.type === 'Funded' || account.type === 'Live') return 'prop';
  const stocks = trades.filter((t) => !isFuturesSymbol(t.symbol)).length;
  return trades.length && stocks > trades.length / 2 ? 'stocks' : 'personal';
}

// How much of each limit is used (0–1), and the one closest to being hit.
function limitUse(account, status) {
  if (!status) return { worst: null, list: [] };
  const list = [];
  if (account.maxLossLimit && status.toBlow !== undefined) list.push({ key: 'max', label: 'Max loss', used: clamp(1 - status.toBlow / account.maxLossLimit) });
  if (account.dailyLossLimit) list.push({ key: 'daily', label: 'Daily loss', used: clamp(Math.max(0, -status.todayNet) / account.dailyLossLimit) });
  if (account.consistencyLimit && status.consistencyPct != null) list.push({ key: 'cons', label: 'Consistency', used: clamp(status.consistencyPct / account.consistencyLimit), best: status.consistencyPct, cap: account.consistencyLimit });
  const worst = list.reduce((w, x) => (!w || x.used > w.used ? x : w), null);
  return { worst, list };
}

// "Daily loss at 55%"; consistency in its own terms: "Best day 63% of profit (cap 55%)".
function limitText(w, t) {
  if (w.key === 'cons') return t('Best day {p}% of profit (cap {cap}%)', { p: Math.round(w.best), cap: w.cap });
  return t('{rule} at {p}', { rule: t(w.label), p: pct(w.used) });
}

const toneOf = (used) => (used >= 0.75 ? 'bad' : used >= 0.5 ? 'warn' : 'good');
const tagOf = (used) => (used >= 1 ? 'Hit' : used >= 0.75 ? 'Danger' : used >= 0.5 ? 'Watch' : 'OK');

function SyncPill({ info }) {
  const { t } = useI18n();
  return (
    <span className={`acct-pill pill-${info.state}`} title={info.message ? t(info.message) : undefined}>
      <i aria-hidden="true" />{t(SYNC_LABEL[info.state])}
    </span>
  );
}

// The gear on every account: everything a trader does with an account, in one menu.
function AccountMenu({ account, info, trades, ctx }) {
  const { t } = useI18n();
  const { canSync, syncing, onSync, onManage, onViewTrades, onUpload, onChanged, onUnlink, onSessionExpired } = ctx;
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(''); // '', 'rename', 'clear', 'delete', 'unlink'
  const [adjusting, setAdjusting] = useState(false);
  const [ending, setEnding] = useState(false);
  const modeRef = useRef('');
  modeRef.current = mode;
  const nameInput = useRef(null);
  useEffect(() => {
    if (mode === 'rename' && nameInput.current) {
      nameInput.current.focus({ preventScroll: true });
      nameInput.current.select();
    }
  }, [mode]);
  const [name, setName] = useState(account.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const box = useRef(null);
  const pop = useRef(null);
  const [place, setPlace] = useState(null);
  const plan = account.platform === 'TopstepX' ? detectPlan(account.externalId || account.name) : null;
  const planRules = plan ? rulesFrom(plan.preset, plan.dll) : {};
  const canLoadRules = plan && plan.type !== 'Personal' && Object.keys(planRules).length > 0 && !(account.maxLossLimit || account.profitTarget || account.dailyLossLimit || account.consistencyLimit);

  // The menu floats over the page (so no card can cut it off), opening below the gear when
  // there's room and above it otherwise, scrolling if the window is short.
  function measure() {
    const r = box.current.getBoundingClientRect();
    const width = Math.min(270, window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8));
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    setPlace(below >= 380 || below >= above
      ? { left, width, top: r.bottom + 6, maxHeight: below - 6 }
      : { left, width, bottom: window.innerHeight - r.top + 6, maxHeight: above - 6 });
  }

  useEffect(() => {
    if (!open) return undefined;
    measure();
    const inside = (e) => (box.current && box.current.contains(e.target)) || (pop.current && pop.current.contains(e.target));
    const onDown = (e) => !inside(e) && close();
    const onKey = (e) => e.key === 'Escape' && close();
    // Scrolling inside the menu is fine. Elsewhere, the plain menu closes, but while renaming
    // or confirming it stays open and follows the gear.
    const onMove = (e) => {
      if (e && e.target && pop.current && pop.current.contains(e.target)) return;
      if (modeRef.current) measure();
      else close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function close() { setOpen(false); setMode(''); setError(''); setName(account.name); }
  const go = (fn) => () => { close(); fn(); };

  async function act(fn) {
    setBusy(true);
    setError('');
    try { await fn(); close(); onChanged(); } catch (err) { if (err.status === 401) return onSessionExpired(); setError(err.message); } finally { setBusy(false); }
  }
  const patch = (body) => api(`/accounts/${account._id}`, { method: 'PATCH', body });
  // Archiving a prop account asks how it ended first (ArchiveAccountDialog).
  function archive() {
    if (!asksHowItEnded(account)) return act(() => patch({ archived: true }));
    close();
    setEnding(true);
  }

  return (
    <div className="acct-menu" ref={box}>
      <button type="button" className={`icon-button acct-gear${open ? ' on' : ''}${syncing && info.kind === 'auto' ? ' spinning' : ''}`} onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="menu" aria-expanded={open} aria-label={t('Options for {name}', { name: account.name })} title={t('Account options')}>
        <Icon name="gear" />
      </button>
      {open && place && createPortal(
        <div className="acct-menu-pop" role="menu" ref={pop} style={{ position: 'fixed', ...place }}>
          <p className="acct-menu-title">{account.name}</p>
          {mode === 'rename' ? (
            <form className="acct-menu-form" onSubmit={(e) => { e.preventDefault(); act(() => patch({ name })); }}>
              <input ref={nameInput} value={name} onChange={(e) => setName(e.target.value)} maxLength={60} aria-label={t('Account name')} />
              <div><button type="submit" className="small" disabled={busy || !name.trim()}>{t('Save')}</button><button type="button" className="link-button" onClick={() => setMode('')}>{t('Cancel')}</button></div>
            </form>
          ) : mode === 'clear' || mode === 'delete' || mode === 'unlink' ? (
            <div className="acct-menu-form">
              {mode === 'delete' && !account.archived && (
                <div className="keep-instead">
                  <strong>{t('Keep it for your journey?')}</strong>
                  <p>{t('Archive hides the account but keeps its trades, what it cost and how it ended in My Trading Journey. Delete erases all of that for good.')}</p>
                  <button type="button" className="small" disabled={busy} onClick={archive}>{t('Archive instead')}</button>
                </div>
              )}
              <p className="acct-menu-warn">
                {mode === 'delete' ? t(trades.length === 1 ? 'Delete {name} and its {n} trade? This can’t be undone.' : 'Delete {name} and its {n} trades? This can’t be undone.', { name: account.name, n: trades.length })
                  : mode === 'clear' ? t(trades.length === 1 ? 'Delete the {n} trade in {name}? The account and its rules stay.' : 'Delete all {n} trades in {name}? The account and its rules stay.', { name: account.name, n: trades.length })
                    : t('Stop syncing {name} with {platform}? Its trades stay, and you can sync it again from Add account.', { name: account.name, platform: info.provider === 'ibkr' ? 'Interactive Brokers' : 'TopstepX' })}
              </p>
              <div>
                <button type="button" className={`small${mode === 'unlink' ? '' : ' danger'}`} disabled={busy}
                  onClick={() => act(() => (mode === 'delete' ? api(`/accounts/${account._id}`, { method: 'DELETE' }) : mode === 'clear' ? api(`/accounts/${account._id}/trades`, { method: 'DELETE' }) : onUnlink(account._id, info.provider)))}>
                  {mode === 'delete' ? t('Yes, delete it') : mode === 'clear' ? t('Yes, delete the trades') : t('Stop syncing')}
                </button>
                <button type="button" className="link-button" onClick={() => setMode('')}>{t('Cancel')}</button>
              </div>
            </div>
          ) : (
            <ul>
              <li><button type="button" role="menuitem" onClick={go(() => onViewTrades(account._id))}>{t('View trades')}</button></li>
              {info.kind === 'auto' && canSync && <li><button type="button" role="menuitem" onClick={go(() => onSync(info.provider))} disabled={syncing}>{t('Sync now')}</button></li>}
              <li><button type="button" role="menuitem" onClick={() => setMode('rename')}>{t('Rename')}</button></li>
              <li><button type="button" role="menuitem" onClick={go(() => onManage(account._id))}>{t('Edit rules & details')}</button></li>
              {canLoadRules && <li><button type="button" role="menuitem" className="hot" onClick={() => act(() => patch({ type: account.type || plan.type, ...(account.size ? {} : plan.size ? { size: plan.size } : {}), ...planRules }))}>◆ {t('Load Topstep {plan} rules', { plan: plan.label })}</button></li>}
              {canAdjustBalance(account) && <li><button type="button" role="menuitem" onClick={go(() => setAdjusting(true))}>{t('Adjust balance')}</button></li>}
              <li><button type="button" role="menuitem" onClick={go(() => onManage(account._id))}>{t('Fees, payouts & new attempt')}</button></li>
              <li><button type="button" role="menuitem" onClick={go(() => onUpload(account._id))}>{t('Upload a trade file')}</button></li>
              {info.kind === 'auto' && <li><button type="button" role="menuitem" onClick={() => setMode('unlink')}>{t('Stop syncing')}</button></li>}
              <li><button type="button" role="menuitem" onClick={account.archived ? () => act(() => patch({ archived: false })) : archive}>{account.archived ? t('Unarchive') : t('Archive')}</button></li>
              <li className="sep" />
              {trades.length > 0 && <li><button type="button" role="menuitem" className="danger" onClick={() => setMode('clear')}>{t('Delete all trades')}</button></li>}
              <li><button type="button" role="menuitem" className="danger" onClick={() => setMode('delete')}>{t('Delete account')}</button></li>
            </ul>
          )}
          {error && <p className="error">{error}</p>}
        </div>,
        document.body,
      )}
      {ending && (
        <ArchiveAccountDialog account={account} trades={trades} onClose={() => setEnding(false)}
          onDone={() => { setEnding(false); onChanged(); }} onSessionExpired={onSessionExpired} />
      )}
      {adjusting && (
        <AdjustBalanceDialog account={account} trades={trades} balance={(accountStatus(account, trades) || {}).balance}
          onClose={() => setAdjusting(false)} onChange={() => onChanged()} onSessionExpired={onSessionExpired} />
      )}
    </div>
  );
}

// One rule's meter on a rule-guarded card.
function RuleMeter({ title, tag, tagTone, value, of, fill, tone, left, right }) {
  const { t } = useI18n();
  return (
    <div className="guard-meter">
      <div className="guard-meter-top"><span>{t(title)}</span>{tag && <em className={`guard-tag tag-${tagTone}`}>{tag}</em>}</div>
      <div className="guard-value"><strong>{value}</strong>{of && <span> / {of}</span>}</div>
      <div className={`acct-meter-bar tone-${tone}`}><span style={{ width: pct(clamp(fill)) }} /></div>
      <div className="guard-meter-foot"><span>{left}</span><span>{right}</span></div>
    </div>
  );
}

// The plain-number tip under a rule-guarded card: room left, in average losses.
function roomTip(account, status, trades, t) {
  const losses = trades.slice(-50).map(netPnl).filter((x) => x < 0);
  if (!losses.length) return null;
  const avg = losses.reduce((s, x) => s - x, 0) / losses.length;
  if (account.dailyLossLimit && status.dailyLeft !== undefined) {
    if (status.dailyLeft <= 0) return { tone: 'bad', text: t('The daily loss limit is used up for today. This account should sit out until the next session.') };
    const n = Math.floor(status.dailyLeft / avg);
    return { tone: n <= 1 ? 'warn' : 'calm', text: n < 1
      ? t('{amount} left before today’s daily loss limit: less than one average loss ({avg}).', { amount: usd.format(status.dailyLeft), avg: usd.format(avg) })
      : t(n === 1 ? '{amount} left before today’s daily loss limit: about one average loss ({avg}).' : '{amount} left before today’s daily loss limit: about {n} average losses ({avg} each).', { amount: usd.format(status.dailyLeft), n, avg: usd.format(avg) }) };
  }
  if (account.maxLossLimit && status.toBlow !== undefined && status.toBlow > 0) {
    const n = Math.floor(status.toBlow / avg);
    return { tone: n <= 2 ? 'warn' : 'calm', text: t(n === 1 ? '{amount} of room before the max loss: about one average loss ({avg}).' : '{amount} of room before the max loss: about {n} average losses ({avg} each).', { amount: usd.format(status.toBlow), n, avg: usd.format(avg) }) };
  }
  return null;
}

function GuardedCard({ account, trades, info, onViewTrades, ctx }) {
  const { t } = useI18n();
  const s = accountStatus(account, trades);
  const badge = PLATFORM_BADGE[account.platform] || PLATFORM_BADGE.Other;
  const tip = s ? roomTip(account, s, trades, t) : null;
  const dailyUsed = s && account.dailyLossLimit ? Math.max(0, -s.todayNet) : 0;
  const maxUsed = s && account.maxLossLimit ? clamp(1 - s.toBlow / account.maxLossLimit) : 0;
  const consUsed = s && account.consistencyLimit && s.consistencyPct != null ? clamp(s.consistencyPct / account.consistencyLimit) : 0;
  return (
    <article className={`guard-card acct-${info.state}`}>
      <div className="guard-body">
        <div className="guard-id">
          <div className="guard-id-top">
            <LogoBadge logo={accountLogo(account)} size="pf-md" className={badge.className} text={badge.text} />
            <div className="acct-card-title">
              <button type="button" className="link-button acct-name" onClick={() => onViewTrades(account._id)}>{account.name}</button>
              <span className="acct-number">{info.link ? info.link.tsxName : account.externalId && account.externalId !== account.name ? maskNumber(account.externalId) : account.platform}</span>
            </div>
          </div>
          <div className="guard-chips">
            {account.type && <span className="guard-chip hot">{t(account.type)}</span>}
            {account.size && <span className="guard-chip">{k(account.size)}</span>}
            <span className="guard-chip">{account.platform || t('Other')}</span>
          </div>
          <p className="guard-label">{t('Balance')}</p>
          <strong className="guard-balance">{s ? usd.format(s.balance) : '—'}</strong>
          {s && <span className={`guard-change ${s.change >= 0 ? 'gain' : 'loss'}`}>{formatMoney(s.change)} {t('since start')} · {t(trades.length === 1 ? '{n} trade' : '{n} trades', { n: trades.length })}</span>}
        </div>

        {s && (
          <div className="guard-meters">
            {account.maxLossLimit ? (
              <RuleMeter title="Max loss room" tag={t(tagOf(maxUsed))} tagTone={toneOf(maxUsed)} value={usd.format(Math.max(0, s.toBlow))} of={k(account.maxLossLimit)}
                fill={s.toBlow / account.maxLossLimit} tone={toneOf(maxUsed)} left={t('{p} left', { p: pct(clamp(s.toBlow / account.maxLossLimit)) })} right={t('floor {amount}', { amount: usd.format(s.floor) })} />
            ) : <div className="guard-meter empty"><span>{t('Max loss room')}</span><small>{t('Not set')}</small></div>}
            {account.dailyLossLimit ? (
              <RuleMeter title="Daily loss" tag={t(tagOf(dailyUsed / account.dailyLossLimit))} tagTone={toneOf(dailyUsed / account.dailyLossLimit)} value={usd.format(dailyUsed)} of={k(account.dailyLossLimit)}
                fill={dailyUsed / account.dailyLossLimit} tone={toneOf(dailyUsed / account.dailyLossLimit)} left={t('{p} used today', { p: pct(clamp(dailyUsed / account.dailyLossLimit)) })} right={t('{amount} left', { amount: usd.format(Math.max(0, s.dailyLeft)) })} />
            ) : <div className="guard-meter empty"><span>{t('Daily loss')}</span><small>{t('Not set')}</small></div>}
            {account.profitTarget ? (
              <RuleMeter title="Profit target" tag={pct(s.progress)} tagTone="target" value={usd.format(Math.max(0, s.change))} of={k(account.profitTarget)}
                fill={s.progress} tone="target" left={s.toPass <= 0 ? t('Reached') : t('{amount} to go', { amount: usd.format(s.toPass) })} right={`→ ${k(s.target)}`} />
            ) : <div className="guard-meter empty"><span>{t('Profit target')}</span><small>{account.type === 'Funded' || account.type === 'Live' ? t('None on funded accounts') : t('Not set')}</small></div>}
            {account.consistencyLimit ? (
              <RuleMeter title="Consistency" tag={s.consistencyPct == null ? null : t(s.consistencyOk ? 'OK' : 'Over')} tagTone={s.consistencyOk ? 'good' : 'bad'}
                value={s.consistencyPct == null ? '—' : `${Math.round(s.consistencyPct)}%`} of={null} fill={consUsed} tone={toneOf(consUsed)}
                left={t('cap {p}%', { p: account.consistencyLimit })} right={s.bestDay ? t('best day {amount}', { amount: usd.format(s.bestDay) }) : t('no profit yet')} />
            ) : <div className="guard-meter empty"><span>{t('Consistency')}</span><small>{t('Not set')}</small></div>}
          </div>
        )}

        <div className="guard-sync">
          <SyncPill info={info} />
          <dl>
            <div><dt>{info.kind === 'auto' ? t('Last sync') : t('Last import')}</dt><dd>{info.last ? clock(info.last) : '—'}</dd></div>
            {info.kind === 'auto' && <div><dt>{t('Next sync')}</dt><dd>{clock(info.next)}</dd></div>}
            <div><dt>{t('Trades')}</dt><dd>{trades.length}</dd></div>
            {info.open > 0 && <div><dt>{t('Open positions')}</dt><dd>{info.open}</dd></div>}
          </dl>
          <div className="guard-actions">
            <AccountMenu account={account} info={info} trades={trades} ctx={ctx} />
          </div>
        </div>
      </div>
      {tip && <p className={`guard-tip tip-${tip.tone}`}><span aria-hidden="true">⚡</span> {tip.text}</p>}
    </article>
  );
}

// A small line of the account's running P&L.
function Sparkline({ trades }) {
  const points = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-80);
  if (points.length < 2) return <div className="spark spark-empty" />;
  let run = 0;
  const ys = points.map((x) => (run += netPnl(x)));
  const min = Math.min(0, ...ys);
  const max = Math.max(0, ...ys);
  const span = max - min || 1;
  const xy = ys.map((y, i) => `${((i / (ys.length - 1)) * 100).toFixed(2)},${(28 - ((y - min) / span) * 26).toFixed(2)}`);
  const up = ys[ys.length - 1] >= 0;
  return (
    <svg className={`spark ${up ? 'up' : 'down'}`} viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
      <polygon points={`0,30 ${xy.join(' ')} 100,30`} />
      <polyline points={xy.join(' ')} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function SimpleCard({ account, trades, info, group, onViewTrades, ctx }) {
  const { t } = useI18n();
  const s = accountStatus(account, trades);
  const net = trades.reduce((sum, x) => sum + netPnl(x), 0);
  const badge = PLATFORM_BADGE[account.platform] || PLATFORM_BADGE.Other;
  return (
    <article className={`acct-card simple-card acct-${info.state}`}>
      <header className="acct-card-head">
        <LogoBadge logo={accountLogo(account)} size="pf-md" className={badge.className} text={badge.text} />
        <div className="acct-card-title">
          <button type="button" className="link-button acct-name" onClick={() => onViewTrades(account._id)}>{account.name}</button>
          <span className="acct-number">{info.link ? t('From {name}', { name: info.link.tsxName }) : account.externalId && account.externalId !== account.name ? maskNumber(account.externalId) : account.platform}</span>
        </div>
        <span className="guard-chip">{group === 'stocks' ? t('Stocks') : account.type ? t(account.type) : t('Personal')}</span>
      </header>
      <div className="simple-stats">
        <div><p className="guard-label">{s ? t('Balance') : t('Net P&L')}</p><strong className={s ? '' : net >= 0 ? 'gain' : 'loss'}>{s ? usd.format(s.balance) : formatMoney(net)}</strong></div>
        <div><p className="guard-label">{t('Trades')}</p><strong>{trades.length}</strong></div>
      </div>
      <Sparkline trades={trades} />
      <footer className="acct-card-foot">
        <SyncPill info={info} />
        <span>{info.last ? when(info.last) : ''}</span>
        <span className="acct-actions">
          <AccountMenu account={account} info={info} trades={trades} ctx={ctx} />
        </span>
      </footer>
    </article>
  );
}

// One account as a row of the List view: built for traders with many accounts.
function ListRow({ account, trades, info, onViewTrades, ctx }) {
  const { t } = useI18n();
  const s = accountStatus(account, trades);
  const net = trades.reduce((sum, x) => sum + netPnl(x), 0);
  const change = s ? s.change : net;
  const { worst } = limitUse(account, s);
  const badge = PLATFORM_BADGE[account.platform] || PLATFORM_BADGE.Other;
  const sub = info.link ? info.link.tsxName : account.externalId && account.externalId !== account.name ? maskNumber(account.externalId) : account.platform;
  return (
    <tr className={`list-row acct-${info.state}`}>
      <td>
        <div className="list-account">
          <LogoBadge logo={accountLogo(account)} className={badge.className} text={badge.text} />
          <span className="list-name">
            <button type="button" className="link-button acct-name" onClick={() => onViewTrades(account._id)}>{account.name}</button>
            <small>{sub}</small>
          </span>
        </div>
      </td>
      <td className="list-type">{account.type ? <span className={`guard-chip${account.type !== 'Personal' ? ' hot' : ''}`}>{t(account.type)}</span> : '—'}</td>
      <td className="list-num">{s ? usd.format(s.balance) : <span className="list-muted">—</span>}</td>
      <td className={`list-num ${change >= 0 ? 'gain' : 'loss'}`}>{formatMoney(change)}</td>
      <td className="list-limit">
        {worst ? (
          <>
            <span className={`tone-text-${toneOf(worst.used)}`}>{limitText(worst, t)}</span>
            <span className={`acct-meter-bar tone-${toneOf(worst.used)}`}><span style={{ width: pct(worst.used) }} /></span>
          </>
        ) : <span className="list-muted">{hasRules(account) ? t('All clear') : t('No rules')}</span>}
      </td>
      <td className="list-num list-trades">{trades.length}</td>
      <td className="list-updated">{info.last ? when(info.last) : '—'}</td>
      <td className="list-sync"><span className={`list-dot dot-${info.state}`} aria-hidden="true" />{info.kind === 'auto' ? t(info.state === 'synced' ? 'Live sync' : info.state === 'error' ? 'Sync error' : 'Needs attention') : t('File import')}</td>
      <td className="list-gear"><AccountMenu account={account} info={info} trades={trades} ctx={ctx} /></td>
    </tr>
  );
}

function ListView({ rows, cardProps }) {
  const { t } = useI18n();
  return (
    <div className="list-wrap">
      <table className="list-table">
        <thead>
          <tr>
            <th>{t('Account')}</th><th className="list-type">{t('Type')}</th><th className="list-num">{t('Balance')}</th><th className="list-num">{t('P&L')}</th>
            <th className="list-limit">{t('Closest limit')}</th><th className="list-num list-trades">{t('Trades')}</th><th className="list-updated">{t('Updated')}</th>
            <th className="list-sync">{t('Source')}</th><th className="list-gear"><span className="visually-hidden">{t('Options')}</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => <ListRow key={r.a._id} account={r.a} trades={r.list} info={r.info} {...cardProps} />)}
        </tbody>
      </table>
    </div>
  );
}

export default function AccountsPage({ user, accounts, trades, onAdd, onManage, onViewTrades, onUpload, onSynced, onSessionExpired }) {
  const { t } = useI18n();
  const [sync, setSync] = useState(null);
  const [ib, setIb] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [tab, setTab] = useState('all');
  const [view, setView] = useState(() => (read(VIEW_KEY, 'cards') === 'cards' ? 'cards' : 'list'));
  const [sort, setSort] = useState(() => read(SORT_KEY, 'limit'));

  useEffect(() => {
    api('/topstepx?lite=1').then(setSync).catch((err) => err.status === 401 && onSessionExpired());
    api('/ibkr').then(setIb).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function syncNow(provider = 'topstepx') {
    setSyncing(true);
    setError('');
    setNotice('');
    try {
      const r = await api(`/${provider}/sync`, { method: 'POST' });
      if (provider === 'ibkr') setIb((s) => ({ ...s, ...r }));
      else setSync((s) => ({ ...s, ...r }));
      setNotice(t('{n} new trades synced.', { n: r.result ? r.result.added || 0 : 0 }));
      onSynced();
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      api('/topstepx?lite=1').then(setSync).catch(() => {});
      api('/ibkr').then(setIb).catch(() => {});
    } finally {
      setSyncing(false);
    }
  }

  const byAccount = {};
  for (const x of trades) (byAccount[x.accountId] = byAccount[x.accountId] || []).push(x);
  const build = (list) => list.map((a) => {
    const list2 = byAccount[a._id] || [];
    const status = accountStatus(a, list2);
    return { a, list: list2, status, info: syncInfo(a, sync, list2, ib), group: groupOf(a, list2), use: limitUse(a, status) };
  });
  const active = build(accounts.filter((x) => !x.archived));
  const archived = build(accounts.filter((x) => x.archived && !x.attemptOf));
  const canSync = Boolean((sync && sync.available) || (ib && ib.available));

  // The summary strip.
  const autoCount = active.filter((r) => r.info.kind === 'auto').length;
  const withBalance = active.filter((r) => r.status);
  const combined = withBalance.reduce((sum, r) => sum + r.status.balance, 0);
  const monthAgo = Date.now() - 30 * 86400000;
  const recent = trades.filter((x) => new Date(x.date).getTime() >= monthAgo && active.some((r) => r.a._id === x.accountId));
  const recentAccounts = new Set(recent.map((x) => x.accountId)).size;
  const watch = active.filter((r) => r.use.worst).sort((a, b) => b.use.worst.used - a.use.worst.used)[0];
  const watchTone = watch ? toneOf(watch.use.worst.used) : 'good';

  // Tabs, sorting and the two sections.
  const counts = { all: active.length, prop: active.filter((r) => r.group === 'prop').length, personal: active.filter((r) => r.group === 'personal').length, stocks: active.filter((r) => r.group === 'stocks').length };
  const shown = active.filter((r) => tab === 'all' || r.group === tab);
  const balanceOf = (r) => (r.status ? r.status.balance : r.list.reduce((sum, x) => sum + netPnl(x), 0));
  const sorters = {
    limit: (a, b) => (b.use.worst ? b.use.worst.used : -1) - (a.use.worst ? a.use.worst.used : -1),
    balance: (a, b) => balanceOf(b) - balanceOf(a),
    updated: (a, b) => String(b.info.last || '').localeCompare(String(a.info.last || '')),
    name: (a, b) => a.a.name.localeCompare(b.a.name),
  };
  const sorted = [...shown].sort(sorters[sort] || sorters.limit);
  const guarded = sorted.filter((r) => r.group === 'prop' && hasRules(r.a) && r.status);
  const others = sorted.filter((r) => !guarded.includes(r));
  const unlink = async (id, provider = 'topstepx') => {
    await api(`/${provider}/links/${id}`, { method: 'DELETE' });
    if (provider === 'ibkr') setIb(await api('/ibkr'));
    else setSync(await api('/topstepx?lite=1'));
  };
  const menuCtx = { canSync, syncing, onSync: syncNow, onManage, onViewTrades, onUpload, onChanged: onSynced, onUnlink: unlink, onSessionExpired };
  const cardProps = { onViewTrades, ctx: menuCtx };

  const problems = active.filter((r) => r.info.state === 'attention');

  return (
    <div className="accounts-page">
      <div className="routine-header">
        <div>
          <h1 className="routine-title">{t('Accounts')}</h1>
          <p className="subtitle">{t('Every account, its balance, and how close you are to each prop firm rule.')}</p>
        </div>
        <button type="button" className="page-action" onClick={onAdd}>+ {t('Add account')}</button>
      </div>

      <section className="acct-stats" aria-label={t('Summary')}>
        <div><p>{t('Active accounts')}</p><strong>{active.length}</strong><small>{archived.length ? t('{n} archived', { n: archived.length }) : t('none archived')}</small></div>
        <div><p>{autoCount > 0 && <i className="live-dot" aria-hidden="true" />}{t('Live sync')}</p><strong>{autoCount}</strong><small>{sync && sync.connected && sync.lastSyncAt ? t('Last sync {when}', { when: when(sync.lastSyncAt) }) : t('File imports only')}</small></div>
        <div><p>{t('Combined balance')}</p><strong>{withBalance.length ? usd.format(combined) : '—'}</strong><small>{t(withBalance.length === 1 ? '{n} account with a starting balance' : '{n} accounts with a starting balance', { n: withBalance.length })}</small></div>
        <div><p>{t('Trades · 30 days')}</p><strong>{recent.length}</strong><small>{t(recentAccounts === 1 ? 'in {n} account' : 'across {n} accounts', { n: recentAccounts })}</small></div>
        <div className={`acct-watch watch-${watchTone}`}>
          <p>{t('Rule watch')}</p>
          {watch && watch.use.worst.used >= 0.5 ? (
            <><strong>⚠ {limitText(watch.use.worst, t)}</strong><small>{watch.a.name}</small></>
          ) : (
            <><strong>{t('All clear')}</strong><small>{watch ? t('Closest: {what} on {name}', { what: limitText(watch.use.worst, t), name: watch.a.name }) : t('No prop rules to watch yet')}</small></>
          )}
        </div>
      </section>

      {sync && sync.lastError && <p className="error">{t(sync.lastError)} <Link to="/app/settings#topstepx">{t('Check your TopstepX connection')}</Link></p>}
      {problems.map(({ a, info }) => (
        <p key={a._id} className="error">
          {t('{n} trades from {tsx} are already in {name}, so they weren’t added again.', { n: info.link.alreadyIn.count, tsx: info.link.tsxName, name: (accounts.find((x) => x._id === info.link.alreadyIn.accountId) || {}).name || t('another account') })}{' '}
          <Link to="/app/settings#topstepx">{t('Fix it in sync settings')}</Link>
        </p>
      ))}
      {notice && <p className="success">{notice}</p>}
      {error && <p className="error">{error}</p>}

      {active.length > 0 && (
        <div className="acct-bar">
          <div className="acct-tabs" role="tablist">
            {[['all', 'All'], ['prop', 'Prop firms'], ['personal', 'Personal'], ['stocks', 'Stocks']].map(([id, label]) => (
              <button type="button" role="tab" key={id} aria-selected={tab === id} className={tab === id ? 'on' : ''} onClick={() => setTab(id)}>
                {t(label)} <span>{counts[id]}</span>
              </button>
            ))}
          </div>
          <div className="acct-tools">
            <label className="acct-sort">{t('Sort')}
              <select value={sort} onChange={(e) => { setSort(e.target.value); write(SORT_KEY, e.target.value); }}>
                <option value="limit">{t('Closest to a limit')}</option>
                <option value="balance">{t('Balance')}</option>
                <option value="updated">{t('Recently updated')}</option>
                <option value="name">{t('Name')}</option>
              </select>
            </label>
            <div className="acct-view" role="group" aria-label={t('View')}>
              {[['cards', 'Cards'], ['list', 'List']].map(([id, label]) => (
                <button type="button" key={id} className={view === id ? 'on' : ''} aria-pressed={view === id} onClick={() => { setView(id); write(VIEW_KEY, id); }}>{t(label)}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {!active.length ? (
        <div className="acct-empty">
          <p>{t('No accounts yet. Add one, import a trade file, or connect TopstepX to sync your trades.')}</p>
        </div>
      ) : !shown.length ? (
        <p className="acct-none">{t('No accounts in this group.')}</p>
      ) : view === 'list' ? (
        <ListView rows={sorted} cardProps={cardProps} />
      ) : (
        <>
          {guarded.length > 0 && (
            <section className="acct-section">
              <h2 className="acct-section-title"><span aria-hidden="true">◆</span> {t('Rule-guarded')} · {guarded.length}</h2>
              <div className="guard-list">
                {guarded.map((r) => <GuardedCard key={r.a._id} account={r.a} trades={r.list} info={r.info} {...cardProps} />)}
              </div>
            </section>
          )}
          {others.length > 0 && (
            <section className="acct-section">
              <h2 className="acct-section-title"><span aria-hidden="true">◇</span> {t('Personal & stocks')} · {others.length} <small>{t('no firm rules: P&L only')}</small></h2>
              <div className="acct-grid">
                {others.map((r) => <SimpleCard key={r.a._id} account={r.a} trades={r.list} info={r.info} group={r.group} {...cardProps} />)}
              </div>
            </section>
          )}
        </>
      )}

      {archived.length > 0 && (
        <div className="acct-archived">
          <button type="button" className="link-button" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? t('Hide archived accounts') : t('Show archived accounts ({n})', { n: archived.length })}
          </button>
          {showArchived && (
            <div className="acct-grid">
              {archived.map((r) => <SimpleCard key={r.a._id} account={r.a} trades={r.list} info={r.info} group={r.group} {...cardProps} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
